// Org-tier subscribers list. One row per customer (business/residential).
// Phase 3a: read + create. Row click links to /subscribers/:id.
//
// Portal parity note: portal renders a live "health" pill derived from
// live SIP registration counts; the org-tier API doesn't surface those
// aggregates yet, so this pass only shows Status.

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { fmtDate } from '../lib/time.js';
import { useAuth } from '../hooks/useAuth.js';
import { canAdmin } from '../lib/roles.js';
import ImportMenu from '../components/shared/ImportMenu.jsx';
import ImportSubscribersModal from './ImportSubscribersModal.jsx';
import ListToolbar from '../components/shared/ListToolbar.jsx';
import LoadingSpinner from '../components/shared/LoadingSpinner.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';
import { HealthPill, CountBadge, toneFromUpTotal, healthFromUpTotal } from '../components/shared/HealthBadges.jsx';
import SortableHeader, { useSort } from '../components/shared/SortableHeader.jsx';
import ActionMenu from '../components/shared/ActionMenu.jsx';
import Pagination, { usePagination } from '../components/shared/Pagination.jsx';
import { useNavigate } from 'react-router-dom';
import { SubscriberFormModal } from './SubscriberDetail.jsx';
import { sharedList } from '../lib/liveQuery.js';

const STATUS_FILTERS = [
  { key: 'all',       label: 'All' },
  { key: 'active',    label: 'Active' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'inactive',  label: 'Inactive' },
];

const TYPE_LABEL = { business: 'Business', residential: 'Residential' };

// Portal ranks these lifecycle states with 'suspended' first for visual
// urgency; we mirror status → StatusBadge semantics instead (the shared
// badge component maps `active/warning/critical/inactive`).
function statusVariant(status) {
  switch (status) {
    case 'active':    return 'active';
    case 'suspended': return 'warning';
    case 'cancelled': return 'critical';
    case 'inactive':  return 'inactive';
    default:          return 'unknown';
  }
}

function TypePill({ type }) {
  const label = TYPE_LABEL[type] || type || '—';
  const cls = type === 'business'
    ? 'bg-blue-100 text-blue-700'
    : 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

// Which lifecycle transitions are legal from each source status.
// Mirrors the server-side validator in lib/lifecycle.js and the
// SubscriberDetail dropdown. cancelled subscribers can only go inactive
// (never re-active — cancellation is durable per feedback_verso_soft_delete).
const NEXT_STATUS = {
  active:    ['suspended', 'cancelled'],
  suspended: ['active', 'cancelled'],
  cancelled: ['inactive'],
  inactive:  ['active'],
};
const STATUS_ACTION_LABEL = {
  active:    'Reactivate',
  suspended: 'Suspend',
  cancelled: 'Cancel',
  inactive:  'Mark inactive',
};

export default function SubscribersList() {
  const { role } = useAuth();
  const canWrite  = canAdmin(role);   // "New Subscriber" — create is admin-only
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);

  // Lifecycle status mutation — same endpoint used by SubscriberDetail's
  // status dropdown. Kebab actions on each row invoke this with the
  // chosen target status.
  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => api.post(`/subscribers/${id}/status`, { status }),
    onSuccess: (_, { name, status }) => {
      queryClient.invalidateQueries({ queryKey: ['subscribers'] });
      setToast({ ok: true, message: `${name} → ${status}` });
      setTimeout(() => setToast(null), 3500);
    },
    onError: (err) => {
      setToast({ ok: false, message: err?.body?.error || err?.message || 'Status change failed' });
      setTimeout(() => setToast(null), 5000);
    },
  });
  const [showCreate, setShowCreate] = useState(false);
  // importOpen = { source, agentId? } — null when the modal is closed.
  const [importOpen, setImportOpen] = useState(null);
  const sort = useSort({ col: 'name', dir: 'asc' });

  // Agents list drives the "Import from PBX host" submenu — one item
  // per agent so an operator can pick which host to import extensions
  // from. Only fetched when the user has write perms since the button
  // is gated on canWrite anyway.
  const { data: agentsData } = useQuery({
    queryKey: ['agents-list'],
    queryFn:  () => api.get('/agents'),
    enabled:  canWrite,
  });
  const agentList = Array.isArray(agentsData) ? agentsData : [];

  const { data, isLoading, error } = useQuery({
    queryKey: ['subscribers', sort.col, sort.dir],
    queryFn: () => api.get('/subscribers', { sort: sort.col, dir: sort.dir }),
    ...sharedList(),
  });

  const createMutation = useMutation({
    mutationFn: (body) => api.post('/subscribers', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscribers'] });
      setShowCreate(false);
    },
  });

  const allRows = useMemo(() => {
    if (Array.isArray(data))              return data;
    if (Array.isArray(data?.rows))        return data.rows;
    if (Array.isArray(data?.subscribers)) return data.subscribers;
    return [];
  }, [data]);

  // Live signals for the Health column + colored count badges: extension
  // registration (from /sip-registrations, keyed per account with its
  // subscriberId) and device online state (from /devices). Both are cheap
  // cached calls; refresh on a slow interval so Health stays roughly live.
  const { data: regData } = useQuery({
    queryKey: ['sip-registrations'],
    queryFn:  () => api.get('/sip-registrations'),
    refetchInterval: 60_000,
    staleTime: 45_000,
  });
  const { data: devData } = useQuery({
    queryKey: ['devices', 'all', 'mac', 'asc'],
    queryFn:  () => api.get('/devices'),
    staleTime: 30_000,
  });

  // Per-subscriber rollup of extension registration + device online state.
  // *Up = in the desired state; *Known = has live data; *Total = all.
  const rollups = useMemo(() => {
    const m = {};
    const ensure = (id) => (m[id] = m[id] || { extUp: 0, extKnown: 0, extTotal: 0, devUp: 0, devKnown: 0, devTotal: 0 });
    for (const entry of Object.values(regData?.registrations || {})) {
      const sid = entry.subscriberId;
      if (sid == null) continue;
      const r = ensure(sid);
      r.extTotal++;
      if (entry.registered != null) { r.extKnown++; if (entry.registered) r.extUp++; }
    }
    const devs = Array.isArray(devData) ? devData : (devData?.rows || devData?.devices || []);
    for (const d of devs) {
      const sid = d.subscriberId ?? d.subscriber_id;
      if (sid == null) continue;
      const r = ensure(sid);
      r.devTotal++;
      if (d.status === 'online' || d.status === 'offline') { r.devKnown++; if (d.status === 'online') r.devUp++; }
    }
    return m;
  }, [regData, devData]);

  // Combine a subscriber's rollup into an overall health verdict.
  function healthFor(id) {
    const r = rollups[id];
    if (!r) return 'na';
    const up = r.extUp + r.devUp;
    const known = r.extKnown + r.devKnown;
    return healthFromUpTotal(up, known);   // 'na' when known === 0
  }

  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const statusCounts = useMemo(() => {
    const c = { all: allRows.length, active: 0, suspended: 0, cancelled: 0, inactive: 0 };
    for (const r of allRows) {
      const k = r.status || 'active';
      if (k in c) c[k]++;
    }
    return c;
  }, [allRows]);
  const rows = useMemo(() => {
    let out = allRows;
    if (statusFilter !== 'all') {
      out = out.filter((r) => (r.status || 'active') === statusFilter);
    }
    const s = search.trim().toLowerCase();
    if (s) {
      out = out.filter((r) => {
        const name = r.display_name || r.name || '';
        const type = r.type || '';
        const phone = r.phone || '';
        const email = r.email || '';
        const billing = r.billing_account || r.billingAccount || '';
        return (
          String(name).toLowerCase().includes(s) ||
          String(type).toLowerCase().includes(s) ||
          String(phone).toLowerCase().includes(s) ||
          String(email).toLowerCase().includes(s) ||
          String(billing).toLowerCase().includes(s)
        );
      });
    }
    return out;
  }, [allRows, statusFilter, search]);

  const { paged: pagedRows, pagination } = usePagination(rows);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Subscribers</h1>
        <div className="flex items-center gap-3">
          {allRows.length > 0 && (
            <span className="text-sm text-gray-500">
              {rows.length === allRows.length
                ? `${allRows.length} subscriber${allRows.length === 1 ? '' : 's'}`
                : `${rows.length} of ${allRows.length}`}
            </span>
          )}
          {canWrite && (
            <ImportMenu
              label="Import subscribers"
              items={[
                ...agentList.map((a) => ({
                  label: `From PBX host: ${a.name || a.agent_id}`,
                  onClick: () => setImportOpen({ source: 'agent', agentId: a.agent_id || a.id }),
                })),
                {
                  label: agentList.length ? '— or —' : 'No PBX hosts yet',
                  disabled: true,
                  onClick: () => {},
                },
                {
                  label: 'From Commio locations',
                  onClick: () => setImportOpen({ source: 'commio' }),
                },
              ]}
            />
          )}
          {canWrite && (
            <button
              onClick={() => { setShowCreate(true); createMutation.reset(); }}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              New Subscriber
            </button>
          )}
        </div>
      </div>

      {showCreate && (
        <SubscriberFormModal
          title="New Subscriber"
          submitLabel="Create"
          onSubmit={(body) => createMutation.mutate(body)}
          onCancel={() => { setShowCreate(false); createMutation.reset(); }}
          isPending={createMutation.isPending}
          error={createMutation.isError ? (createMutation.error?.body?.error || createMutation.error?.message || 'Create failed') : null}
        />
      )}

      {importOpen && (
        <ImportSubscribersModal
          source={importOpen.source}
          agentId={importOpen.agentId}
          onClose={() => setImportOpen(null)}
        />
      )}

      <ListToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Filter by name, phone, email, billing…"
        filters={STATUS_FILTERS}
        filterCounts={statusCounts}
        activeFilter={statusFilter}
        onFilterChange={setStatusFilter}
      />

      {toast && (
        <div className={`rounded-md border px-4 py-2 text-sm ${
          toast.ok ? 'border-green-200 bg-green-50 text-green-700'
                   : 'border-red-200 bg-red-50 text-red-700'
        }`}>
          {toast.message}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error.message}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white">
        {isLoading ? (
          <LoadingSpinner message="Loading subscribers..." />
        ) : rows.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-sm text-gray-500">No subscribers yet.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <SortableHeader col="status"  sort={sort} label="Status" />
                <SortableHeader col="type"    sort={sort} label="Type" />
                <SortableHeader col="name"    sort={sort} label="Name" />
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Health</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Locations</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Extensions</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">DIDs</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Devices</th>
                <SortableHeader col="created" sort={sort} label="Created" defaultDir="desc" />
                {canWrite && <th className="px-2 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pagedRows.map((r) => {
                const id = r.id || r.subscriber_id;
                const name = r.display_name || r.name || '(unnamed)';
                const status = r.status || 'active';
                const roll = rollups[id] || {};
                const locCount = r.location_count ?? r.locationCount ?? 0;
                const extCount = r.extension_count ?? r.extensionCount ?? 0;
                const didCount = r.did_count ?? r.didCount ?? 0;
                const devCount = r.device_count ?? r.deviceCount ?? 0;
                const health = healthFor(id);
                return (
                  <tr key={id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <StatusBadge status={statusVariant(status)} label={status} />
                    </td>
                    <td className="px-4 py-2">
                      <TypePill type={r.type} />
                    </td>
                    <td className="px-4 py-2 font-medium">
                      <Link to={`/subscribers/${id}`} className="text-blue-600 hover:underline">
                        {name}
                      </Link>
                    </td>
                    <td className="px-4 py-2">
                      <HealthPill
                        health={health}
                        title={`Extensions ${roll.extUp ?? 0}/${roll.extKnown ?? 0} registered · Devices ${roll.devUp ?? 0}/${roll.devKnown ?? 0} online`}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <CountBadge count={locCount} tone={locCount ? 'good' : 'neutral'} title="Locations" />
                    </td>
                    <td className="px-4 py-2">
                      <CountBadge
                        count={extCount}
                        tone={toneFromUpTotal(roll.extKnown ? roll.extUp : null, roll.extKnown ?? 0)}
                        title={`${roll.extUp ?? 0} of ${roll.extKnown ?? 0} registered`}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <CountBadge count={didCount} tone={didCount ? 'good' : 'neutral'} title="DIDs assigned" />
                    </td>
                    <td className="px-4 py-2">
                      <CountBadge
                        count={devCount}
                        tone={toneFromUpTotal(roll.devKnown ? roll.devUp : null, roll.devKnown ?? 0)}
                        title={`${roll.devUp ?? 0} of ${roll.devKnown ?? 0} online`}
                      />
                    </td>
                    <td className="px-4 py-2 text-gray-500">
                      {r.created_at
                        ? fmtDate(r.created_at)
                        : '—'}
                    </td>
                    {canWrite && (
                      <td className="px-2 py-2 text-right">
                        <ActionMenu
                          title={`Actions for ${name}`}
                          items={[
                            { label: 'Open',    onClick: () => navigate(`/subscribers/${id}`) },
                            { divider: true },
                            ...(() => {
                              const opts = [...(NEXT_STATUS[status] || [])];
                              // Nothing attached? Offer the direct → inactive
                              // shortcut (then it can be deleted). With
                              // resources, cancel first — server enforces this.
                              const empty = ((r.did_count ?? r.didCount ?? 0)
                                + (r.extension_count ?? r.extensionCount ?? 0)
                                + (r.device_count ?? r.deviceCount ?? 0)) === 0;
                              if (empty && (status === 'active' || status === 'suspended')) opts.push('inactive');
                              return opts;
                            })().map((next) => ({
                              label: STATUS_ACTION_LABEL[next] || next,
                              onClick: () => statusMutation.mutate({ id, name, status: next }),
                              disabled: statusMutation.isPending,
                              danger: next === 'cancelled',
                            })),
                          ]}
                        />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <Pagination {...pagination} />
      </div>
    </div>
  );
}
